import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import knowledgeIcon from '../../../../../assets/images/chatbot_ai/memory_icon.svg';
import NoActivityPlaceholder from './NoActivityPlaceholder';
import KnowledgeModel from '../../../../models/KnowledgeModel';
import { formatSummaryDateWithSeconds } from '../../../../../util/dateUtils';
import ButtonComponent from '../../../../components/ButtonMC';
import LoadingMC from '../../../../components/LoadingMC';

const ITEMS_PER_PAGE = 4;

const Knowledges = ({ refreshKey, customerId }) => {
  const { t } = useTranslation();
  const [knowledges, setKnowledges] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const { customer } = useSelector((reduxState) => reduxState.mainReducer);

  const customerIdToUse = customerId !== undefined ? customerId : customer?.id;

  const hasNextPage = knowledges.length < total;

  useEffect(() => {
    if (!customerIdToUse) {
      return;
    }

    setLoading(true);
    const params = {
      customer_id: String(customerIdToUse),
      limit: ITEMS_PER_PAGE,
      skip: 0
    };

    KnowledgeModel.get(params)
      .then((response) => {
        const knowledgesData = response?.data || [];
        const totalCount = response?.total || 0;

        setKnowledges(knowledgesData);
        setTotal(totalCount);
      })
      .catch((error) => {
        console.error('Error fetching knowledges', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [customerId, customer, refreshKey, customerIdToUse]);

  const loadMoreKnowledges = useCallback(() => {
    if (!customerIdToUse || loading || !hasNextPage) {
      return;
    }

    setLoading(true);
    const params = {
      customer_id: String(customerIdToUse),
      limit: ITEMS_PER_PAGE,
      skip: knowledges.length
    };

    KnowledgeModel.get(params)
      .then((response) => {
        const knowledgesData = response?.data || [];
        const totalCount = response?.total ?? total;

        setKnowledges((prev) => [...prev, ...knowledgesData]);
        setTotal(totalCount);
      })
      .catch((error) => {
        console.error('Error fetching knowledges', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [customerIdToUse, hasNextPage, knowledges.length, loading, total]);

  if (!loading && total === 0) {
    return <NoActivityPlaceholder />;
  }

  return (
    <div className="tw-flex tw-flex-col tw-gap-2 tw-mt-4">
      <LoadingMC loading={loading && knowledges.length === 0} />
      <ul className="p-0 tw-m-0 tw-flex-shrink-0">
        {knowledges.map((knowledge, index) => (
          <li className="d-flex pb-24" key={knowledge.id}>
            <div className="d-flex justify-content-center position-relative w-32">
              <div className="d-flex h-32 justify-content-center w-32" style={{ borderRadius: '50%' }}>
                <img src={knowledgeIcon} alt="Knowledge" />
              </div>
              {index !== knowledges.length - 1 && (
                <div className="h-perc-99 mt-32 position-absolute w-2" style={{ backgroundColor: 'lightblue' }} />
              )}
            </div>

            <div className="d-flex flex flex-column pl-8">
              <span className="fz-12 mb-4">
                {knowledge.key}
                :
                {' '}
                {knowledge.value}
              </span>
              <span className="fz-11 mt-2" style={{ color: '#6b7280' }}>
                {formatSummaryDateWithSeconds(knowledge.updated_at || knowledge.created_at)}
              </span>
            </div>
          </li>
        ))}
      </ul>

      {hasNextPage && knowledges.length > 0 && (
        <div className="tw-flex tw-justify-center tw-mt-4 tw-mb-2 tw-flex-shrink-0">
          <ButtonComponent
            outlined
            size="xs"
            variant="SECONDARY"
            className="tw-border-0 tw-bg-transparent hover:tw-bg-transparent tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-px-2 tw-py-1"
            iconClassName="!tw-w-4 !tw-h-4 tw-flex-shrink-0 tw-text-blue-500"
            text={loading ? t('InboxMC:event.loading') : t('InboxMC:event.showMore')}
            icon="arrow-ios-downward-outline"
            onClick={loadMoreKnowledges}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
};

export default Knowledges;
